import { ActionSheetButton, IonBackButton, IonButton, IonButtons, IonCard, IonCol, IonContent, IonHeader, IonList, IonRow, IonText, IonTitle, IonToolbar, useIonActionSheet } from '@ionic/react';
import { IonPageWrapper } from './AutoSyncPage.styles';

import ClockingHistoryCard from '../../components/clockingHistoryCard/ClockingHistoryCard';
import TechnicianCommentItem from '../../components/technicianCommentsCard/technicianCommentItem/TechnicianCommentItem';
import { IBranch } from '../../models/dms/branch.model';
import { ClockingDirection, ClockingType, IAttendanceClocking, IClockingHistory, ILabourClocking } from '../../models/dms/clocking.model';
import { IClockingRequestQueueItem, ITechCommentRequestQueueItem, IVehicleAttachmentRequestQueueItem } from '../../models/dms/requestQueue.model';
import { IWorkCategory } from '../../models/dms/technician.model';
import { ITypeCode } from '../../models/dms/typecode.model';
import { EmptyOdataResult } from '../../models/odata.model';
import { RouteNames } from '../../models/routes.model';
import { useDataSync } from '../../providers/DataSyncProvider';
import store from '../../store';
import { setBranchesHasSyncStoreAsync, setBranchesSyncLoadingState, setBranchesSyncStoreAsync } from '../../store/branchSyncSlice';
import { setDelayCodesHasSyncStoreAsync, setDelayCodesSyncLoadingState, setDelayCodesSyncStoreAsync } from '../../store/delayCodeSyncSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setRepairOrderHasSyncStoreAsync, setRepairOrderSyncLoadingState, setRepairOrderSyncStoreAsync } from '../../store/repairOrderSyncSlice';
import { removeFromRequestQueueStoreAsync } from '../../store/requestQueueSlice';
import { setEnableOfflineModeStoreAsync } from '../../store/settingsSlice';
import { setWorkCategoriesHasSyncStoreAsync, setWorkCategoriesSyncLoadingState, setWorkCategoriesSyncStoreAsync } from '../../store/workCategorySyncSlice';
import BranchesSyncItem from './repairOrdersSyncItem/BranchesSyncItem';
import DelayCodesSyncItem from './repairOrdersSyncItem/DelayCodesSyncItem';
import RepairOrdersSyncItem from './repairOrdersSyncItem/RepairOrdersSyncItem';
import WorkCategoriesSyncItem from './repairOrdersSyncItem/WorkCatgegoriesSyncItem';
import { AttachmentAction, AttachmentAssetType } from '../../models/dms/attachment.model';
import VehicleAttachment from '../../components/vehicleAttachment/VehicleAttachment';
import MediaSettingsSyncItem from './repairOrdersSyncItem/MediaSettingsSyncItem';
import FranchisesSyncItem from './repairOrdersSyncItem/FranchisesSyncItem';
import { setFranchisesHasSyncStoreAsync, setFranchisesSyncLoadingState } from '../../store/franchiseSyncSlice';

interface IComponentProps {}

const AutoSyncPage: React.FC<IComponentProps> = () => {
  const { clockingQueue, techCommentQueue, vehicleAttachmentQueue } = useAppSelector((state) => state.requestQueue);

  const [presentActionSheet] = useIonActionSheet();
  const {
    state: { repairOrderSyncTimer, delayCodesSyncTimer, workCategoriesSyncTimer, branchesSyncTimer, franchisesSyncTimer },
  } = useDataSync();

  const dispatch = useAppDispatch();

  const requestQueueAsClockingHistory = (requestQueue: IClockingRequestQueueItem) => {
    let newClockingHistory = {
      type: requestQueue.data.type,
      direction: requestQueue.data.direction,
      unSync: true,
      id: requestQueue.id,
    } as IClockingHistory;

    if (requestQueue.data.type === ClockingType.Attendance) {
      const attendanceClocking = requestQueue.data as IAttendanceClocking;
      const dt = requestQueue.data.direction === ClockingDirection.On ? attendanceClocking.startDateTime : attendanceClocking.endDateTime;
      newClockingHistory = {
        ...newClockingHistory,
        clockingDateTime: dt,
      } as IClockingHistory;
    } else {
      const labourClocking = requestQueue.data as ILabourClocking;
      const dt = requestQueue.data.direction === ClockingDirection.On ? labourClocking.startDateTime : labourClocking.endDateTime;
      newClockingHistory = {
        ...newClockingHistory,
        branchCode: labourClocking.jobData.branchCode,
        jobCode: labourClocking.jobData.jobCode,
        jobType: labourClocking.jobData.jobType,
        repairOrderNumber: labourClocking.jobData.repairOrderNumber,
        clockingDateTime: dt,
      } as IClockingHistory;
    }

    return newClockingHistory;
  };

  const handleOptionsButtonClick = () => {
    let buttons = [
      {
        text: 'Clear Sync Cache',
        handler: async () => {
          await clearSyncCache();
          await fireTimers();
        },
      },
    ] as ActionSheetButton<any>[];

    const { enableOfflineMode } = store.getState().settings;

    if (enableOfflineMode) {
      buttons.push({
        text: 'Disable Offline Mode',
        handler: () => onSelectOffline(false),
      });
    } else {
      buttons.push({
        text: 'Enable Offline Mode',
        handler: () => onSelectOffline(true),
      });
    }

    presentActionSheet({
      header: 'Options',
      buttons,
    });
  };

  const clearSyncCache = async () => {
    // When clearing repair orders, keep any adhoc repair orders
    const adhocRos = store.getState().repairOrderSync.repairOrderSync?.data.filter((ro) => ro.isAdhoc) || [];
    await dispatch(
      setRepairOrderSyncStoreAsync({
        data: adhocRos.length ? [...adhocRos] : [],
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
    await dispatch(
      setDelayCodesSyncStoreAsync({
        data: EmptyOdataResult<ITypeCode>(),
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
    await dispatch(
      setWorkCategoriesSyncStoreAsync({
        data: EmptyOdataResult<IWorkCategory>(),
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
    await dispatch(
      setBranchesSyncStoreAsync({
        data: EmptyOdataResult<IBranch>(),
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
  };

  const onSelectOffline = async (enable: boolean) => {
    if (enable) {
      await dispatch(setEnableOfflineModeStoreAsync(enable));
      await fireTimers();
    } else {
      await dispatch(setEnableOfflineModeStoreAsync(enable));
      await clearSyncCache();
    }
  };

  const fireTimers = async () => {
    await dispatch(setRepairOrderSyncLoadingState(true));
    await dispatch(setRepairOrderHasSyncStoreAsync(false));
    repairOrderSyncTimer?.fire();
    await dispatch(setDelayCodesSyncLoadingState(true));
    await dispatch(setDelayCodesHasSyncStoreAsync(false));
    delayCodesSyncTimer?.fire();
    await dispatch(setWorkCategoriesSyncLoadingState(true));
    await dispatch(setWorkCategoriesHasSyncStoreAsync(false));
    workCategoriesSyncTimer?.fire();
    await dispatch(setBranchesSyncLoadingState(true));
    await dispatch(setBranchesHasSyncStoreAsync(false));
    branchesSyncTimer?.fire();
    await dispatch(setFranchisesSyncLoadingState(true));
    await dispatch(setFranchisesHasSyncStoreAsync(false));
    franchisesSyncTimer?.fire();
  };

  const handleClickRequestQueueItem = async (queueItem: IClockingRequestQueueItem | ITechCommentRequestQueueItem | IVehicleAttachmentRequestQueueItem) => {
    await presentActionSheet({
      header: 'Sync Options',
      buttons: [
        {
          text: 'Delete',
          handler: async () => {
            await dispatch(removeFromRequestQueueStoreAsync(queueItem));
          },
        },
      ],
    });
  };

  return (
    <IonPageWrapper>
      <IonHeader color="lightest">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref={RouteNames.settings} color="secondary" />
          </IonButtons>
          <IonTitle>Auto Sync</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" color="secondary" onClick={() => handleOptionsButtonClick()}>
              Options
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent color="lightest">
        <IonList>
          <RepairOrdersSyncItem title="Repair Orders" />
          <DelayCodesSyncItem title="Delay Codes" />
          <WorkCategoriesSyncItem title="Work Categories" />
          <BranchesSyncItem title="Branches" />
          <FranchisesSyncItem title="Franchises" />
          <MediaSettingsSyncItem title="Media Settings" />

          {clockingQueue?.length ? (
            <>
              <p className="ion-text-center">
                <IonText color="warning">Sync Clockings</IonText>
              </p>
              <IonRow>
                {clockingQueue.map((queueItem) => {
                  return (
                    <IonCol key={queueItem.id} class="flexed-fill flexed" onClick={() => handleClickRequestQueueItem(queueItem)}>
                      <ClockingHistoryCard clockingHistory={requestQueueAsClockingHistory(queueItem)} handleClick={() => {}} errorMessage={queueItem.error} />
                    </IonCol>
                  );
                })}
              </IonRow>
            </>
          ) : (
            <></>
          )}

          {techCommentQueue?.length ? (
            <>
              <p className="ion-text-center">
                <IonText color="warning">Sync Comments</IonText>
              </p>
              <IonRow>
                {techCommentQueue.map((queueItem) => {
                  return (
                    <IonCol key={queueItem.id} class="flexed-fill flexed col" onClick={() => handleClickRequestQueueItem(queueItem)}>
                      <IonCard>
                        <TechnicianCommentItem techComment={queueItem.data} onClick={() => {}} errorMessage={queueItem.error} lines="none" />
                      </IonCard>
                    </IonCol>
                  );
                })}
              </IonRow>
            </>
          ) : (
            <></>
          )}

          {vehicleAttachmentQueue?.length ? (
            <>
              <p className="ion-text-center">
                <IonText color="warning">Sync Media</IonText>
              </p>
              <IonRow>
                {vehicleAttachmentQueue.map((queueItem) => {
                  return (
                    <IonCol key={queueItem.id} class="flexed-fill flexed col" onClick={() => handleClickRequestQueueItem(queueItem)}>
                      <IonCard>
                        {queueItem.data.attachments.map((attachment) => {
                          if (attachment.action === AttachmentAction.Add) {
                            return <VehicleAttachment attachment={attachment} attachmentType={attachment.assetType || AttachmentAssetType.WorkshopVehicle} assetKey={''} handleClick={() => {}} />;
                          }
                          return undefined;
                        })}
                      </IonCard>
                    </IonCol>
                  );
                })}
              </IonRow>
            </>
          ) : (
            <></>
          )}
        </IonList>
      </IonContent>
    </IonPageWrapper>
  );
};

export default AutoSyncPage;
